import React, { useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useBox } from '@react-three/cannon';
import * as THREE from 'three';

// Global shared state for dynamic obstacle positions (animals and pedestrians)
export const globalObstacles = {};

// Custom hook for Circular Vehicle Navigation and Obstacle Avoidance
const useVehicleNavigation = (initialPosition, radius, direction, speed, api, positionRef) => {
  const currentAngle = useRef(Math.atan2(initialPosition[2], initialPosition[0]));
  
  useFrame((state, delta) => {
    let shouldStop = false;
    const currentPos = positionRef.current;
    
    // Obstacle Avoidance Raycasting (Simulated via distance + dot product)
    for (const key in globalObstacles) {
      const obs = globalObstacles[key];
      // Check if obstacle is within 8 meters
      if (currentPos.distanceTo(obs) < 8.0) {
        const toObs = obs.clone().sub(currentPos).normalize();
        
        // Predict where the vehicle is heading next to establish its forward vector
        const nextAngleTest = currentAngle.current + direction * 0.1;
        const moveDir = new THREE.Vector3(
          Math.cos(nextAngleTest) * radius - currentPos.x,
          0,
          Math.sin(nextAngleTest) * radius - currentPos.z
        ).normalize();
        
        // If the obstacle is mostly in front of the vehicle (dot > 0.6)
        if (toObs.dot(moveDir) > 0.6) {
          shouldStop = true;
          break;
        }
      }
    }
    
    if (!shouldStop) {
      // Circular Math Navigation (Forces vehicles perfectly onto the road curve)
      currentAngle.current += direction * (speed * delta / radius);
      
      const nextX = Math.cos(currentAngle.current) * radius;
      const nextZ = Math.sin(currentAngle.current) * radius;
      
      const dirX = nextX - currentPos.x;
      const dirZ = nextZ - currentPos.z;
      
      currentPos.set(nextX, currentPos.y, nextZ);
      api.position.set(nextX, currentPos.y, nextZ);
      
      // Calculate tangent rotation
      const targetRotation = Math.atan2(dirX, dirZ);
      api.rotation.set(0, targetRotation, 0);
    }
  });
};

// Procedural Autorickshaw (Yellow and Green blocky vehicle)
export const Autorickshaw = ({ initialPosition, radius = 30, direction = 1, speed = 4 }) => {
  const [ref, api] = useBox(() => ({
    type: 'Kinematic',
    position: [initialPosition[0], 0.8, initialPosition[2]],
    args: [1.5, 2.0, 2.2], // Bounding box for the rickshaw
    mass: 500, // Heavy object
  }));

  const positionRef = useRef(new THREE.Vector3(initialPosition[0], 0.76, initialPosition[2]));

  useVehicleNavigation(initialPosition, radius, direction, speed, api, positionRef);

  return (
    <group ref={ref}>
      {/* --- CHASSIS (Green) --- */}
      {/* Main Floor / Base */}
      <mesh position={[0, -0.5, -0.2]} castShadow receiveShadow>
        <boxGeometry args={[1.4, 0.2, 1.8]} />
        <meshStandardMaterial color="#2e8b57" roughness={0.7} metalness={0.4} />
      </mesh>
      
      {/* Front Nose (Sloped) */}
      <mesh position={[0, -0.3, 0.9]} rotation={[0.4, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.0, 0.6, 0.8]} />
        <meshStandardMaterial color="#2e8b57" roughness={0.7} metalness={0.4} />
      </mesh>
      
      {/* Front Wheel Mudguard */}
      <mesh position={[0, -0.6, 1.15]} rotation={[0.2, 0, 0]} castShadow>
        <boxGeometry args={[0.3, 0.3, 0.5]} />
        <meshStandardMaterial color="#2e8b57" roughness={0.7} metalness={0.4} />
      </mesh>

      {/* --- CABIN & ROOF (Yellow/Black) --- */}
      {/* Back Wall */}
      <mesh position={[0, 0.2, -1.05]} castShadow receiveShadow>
        <boxGeometry args={[1.4, 1.4, 0.1]} />
        <meshStandardMaterial color="#ffc107" roughness={0.5} />
      </mesh>
      {/* Back Window Frame */}
      <mesh position={[0, 0.5, -1.06]} castShadow>
        <boxGeometry args={[1.0, 0.6, 0.1]} />
        <meshStandardMaterial color="#111111" roughness={0.1} metalness={0.9} />
      </mesh>

      {/* Side Pillars */}
      <mesh position={[-0.65, 0.2, -0.2]} castShadow>
        <boxGeometry args={[0.1, 1.4, 0.2]} />
        <meshStandardMaterial color="#ffc107" roughness={0.5} />
      </mesh>
      <mesh position={[0.65, 0.2, -0.2]} castShadow>
        <boxGeometry args={[0.1, 1.4, 0.2]} />
        <meshStandardMaterial color="#ffc107" roughness={0.5} />
      </mesh>
      {/* Front Pillars (Sloped for windshield) */}
      <mesh position={[-0.45, 0.3, 0.6]} rotation={[-0.3, 0, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 1.2]} />
        <meshStandardMaterial color="#333333" roughness={0.8} />
      </mesh>
      <mesh position={[0.45, 0.3, 0.6]} rotation={[-0.3, 0, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 1.2]} />
        <meshStandardMaterial color="#333333" roughness={0.8} />
      </mesh>
      
      {/* Windshield Glass */}
      <mesh position={[0, 0.45, 0.65]} rotation={[-0.3, 0, 0]} castShadow>
        <planeGeometry args={[0.9, 0.9]} />
        <meshPhysicalMaterial color="#ffffff" transparent opacity={0.3} roughness={0.1} metalness={0.8} />
      </mesh>
      {/* Windshield Wipers (Small blocks) */}
      <mesh position={[-0.2, 0.2, 0.72]} rotation={[-0.3, 0, -0.4]} castShadow>
        <boxGeometry args={[0.02, 0.3, 0.01]} />
        <meshStandardMaterial color="#111" />
      </mesh>
      <mesh position={[0.2, 0.2, 0.72]} rotation={[-0.3, 0, -0.4]} castShadow>
        <boxGeometry args={[0.02, 0.3, 0.01]} />
        <meshStandardMaterial color="#111" />
      </mesh>

      {/* Canvas Roof (Yellow) */}
      <mesh position={[0, 0.9, -0.2]} castShadow>
        <boxGeometry args={[1.45, 0.1, 1.8]} />
        <meshStandardMaterial color="#ffcc00" roughness={0.9} />
      </mesh>
      {/* Roof front slope (Yellow) */}
      <mesh position={[0, 0.85, 0.65]} rotation={[-0.2, 0, 0]} castShadow>
        <boxGeometry args={[1.45, 0.08, 0.4]} />
        <meshStandardMaterial color="#ffcc00" roughness={0.9} />
      </mesh>
      
      {/* Rearview Mirrors (Small blocks) */}
      <mesh position={[-0.55, 0.4, 0.6]} castShadow><boxGeometry args={[0.1, 0.1, 0.1]} /><meshStandardMaterial color="#333" /></mesh>
      <mesh position={[-0.65, 0.4, 0.6]} castShadow><boxGeometry args={[0.05, 0.15, 0.1]} /><meshStandardMaterial color="#222" /></mesh>
      <mesh position={[0.55, 0.4, 0.6]} castShadow><boxGeometry args={[0.1, 0.1, 0.1]} /><meshStandardMaterial color="#333" /></mesh>
      <mesh position={[0.65, 0.4, 0.6]} castShadow><boxGeometry args={[0.05, 0.15, 0.1]} /><meshStandardMaterial color="#222" /></mesh>

      {/* --- INTERIOR --- */}
      {/* Driver Seat */}
      <mesh position={[0, -0.2, 0.1]} castShadow>
        <boxGeometry args={[0.6, 0.15, 0.4]} />
        <meshStandardMaterial color="#111111" roughness={0.8} />
      </mesh>
      {/* Driver Backrest */}
      <mesh position={[0, 0, -0.05]} castShadow>
        <boxGeometry args={[0.6, 0.4, 0.1]} />
        <meshStandardMaterial color="#111111" roughness={0.8} />
      </mesh>
      {/* Steering Column & Handlebars */}
      <mesh position={[0, 0.1, 0.5]} rotation={[-0.5, 0, 0]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.6]} />
        <meshStandardMaterial color="#555555" metalness={0.8} />
      </mesh>
      <mesh position={[0, 0.3, 0.35]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 0.6]} />
        <meshStandardMaterial color="#111111" />
      </mesh>
      {/* Fare Meter (Small block) */}
      <mesh position={[0.3, 0.1, 0.6]} castShadow>
        <boxGeometry args={[0.15, 0.15, 0.1]} />
        <meshStandardMaterial color="#222" />
      </mesh>
      
      {/* Passenger Bench */}
      <mesh position={[0, -0.2, -0.7]} castShadow>
        <boxGeometry args={[1.2, 0.15, 0.6]} />
        <meshStandardMaterial color="#331111" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.1, -0.95]} castShadow>
        <boxGeometry args={[1.2, 0.5, 0.15]} />
        <meshStandardMaterial color="#331111" roughness={0.9} />
      </mesh>

      {/* --- DETAILS (Lights, Bumpers) --- */}
      {/* Single Front Headlight */}
      <group position={[0, -0.1, 1.25]}>
        <mesh>
          <cylinderGeometry args={[0.1, 0.1, 0.15]} rotation={[Math.PI / 2, 0, 0]} />
          <meshStandardMaterial color="#cccccc" metalness={0.9} roughness={0.2} />
        </mesh>
        <mesh position={[0, 0, 0.08]}>
          <circleGeometry args={[0.09, 16]} />
          <meshBasicMaterial color="#ffffee" />
        </mesh>
      </group>
      {/* Turn Signals (Small orange blocks) */}
      <mesh position={[-0.3, -0.15, 1.15]}><boxGeometry args={[0.05, 0.05, 0.1]} /><meshBasicMaterial color="#ff8800" /></mesh>
      <mesh position={[0.3, -0.15, 1.15]}><boxGeometry args={[0.05, 0.05, 0.1]} /><meshBasicMaterial color="#ff8800" /></mesh>
      
      {/* Taillights */}
      <mesh position={[-0.5, -0.2, -1.11]}>
        <boxGeometry args={[0.15, 0.1, 0.05]} />
        <meshBasicMaterial color="#ff0000" />
      </mesh>
      <mesh position={[0.5, -0.2, -1.11]}>
        <boxGeometry args={[0.15, 0.1, 0.05]} />
        <meshBasicMaterial color="#ff0000" />
      </mesh>
      
      {/* Front Bumper */}
      <mesh position={[0, -0.5, 1.3]} castShadow>
        <boxGeometry args={[0.6, 0.1, 0.1]} />
        <meshStandardMaterial color="#333333" />
      </mesh>
      {/* License Plate (Front and Back) */}
      <mesh position={[0, -0.5, 1.36]}><boxGeometry args={[0.3, 0.08, 0.01]} /><meshBasicMaterial color="#fff" /></mesh>
      <mesh position={[0, -0.5, -1.11]}><boxGeometry args={[0.4, 0.1, 0.01]} /><meshBasicMaterial color="#ffcc00" /></mesh>

      {/* --- WHEELS --- */}
      {/* Front Wheel */}
      <group position={[0, -0.7, 1.15]}>
        <mesh rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.22, 0.22, 0.15, 24]} />
          <meshStandardMaterial color="#111111" roughness={0.9} />
        </mesh>
        <mesh rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.12, 0.12, 0.16, 16]} /><meshStandardMaterial color="#cccccc" /></mesh>
      </group>
      
      {/* Left Back Wheel */}
      <group position={[-0.75, -0.7, -0.7]}>
        <mesh rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.22, 0.22, 0.2, 24]} />
          <meshStandardMaterial color="#111111" roughness={0.9} />
        </mesh>
        <mesh rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.12, 0.12, 0.21, 16]} /><meshStandardMaterial color="#cccccc" /></mesh>
      </group>
      
      {/* Right Back Wheel */}
      <group position={[0.75, -0.7, -0.7]}>
        <mesh rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.22, 0.22, 0.2, 24]} />
          <meshStandardMaterial color="#111111" roughness={0.9} />
        </mesh>
        <mesh rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.12, 0.12, 0.21, 16]} /><meshStandardMaterial color="#cccccc" /></mesh>
      </group>

    </group>
  );
};

// Procedural Cow (White and Black blocky animal)
export const Cow = ({ initialPosition, radius = 5, speed = 1 }) => {
  const [ref, api] = useBox(() => ({
    type: 'Kinematic',
    position: [initialPosition[0], 0.8, initialPosition[2]],
    args: [0.8, 1.5, 1.6],
    mass: 100,
  }));
  
  const positionRef = useRef(new THREE.Vector3(initialPosition[0], 0.8, initialPosition[2]));
  const currentRotationY = useRef(0);
  
  // Register in global obstacles
  const uuid = useRef(THREE.MathUtils.generateUUID());
  useEffect(() => {
    return () => delete globalObstacles[uuid.current];
  }, []);
  
  const targetPos = useRef(new THREE.Vector3(
    initialPosition[0] + (Math.random() - 0.5) * radius,
    initialPosition[1],
    initialPosition[2] + (Math.random() - 0.5) * radius
  ));
  
  const idleTimer = useRef(0);

  useFrame((state, delta) => {
    globalObstacles[uuid.current] = positionRef.current.clone();

    if (idleTimer.current > 0) {
      idleTimer.current -= delta;
      return;
    }

    const currentPos = positionRef.current;
    const horizontalDist = new THREE.Vector2(currentPos.x, currentPos.z).distanceTo(new THREE.Vector2(targetPos.current.x, targetPos.current.z));

    if (horizontalDist < 0.5) {
      if (Math.random() > 0.5) {
        idleTimer.current = 2 + Math.random() * 4;
      } else {
        targetPos.current.set(
          initialPosition[0] + (Math.random() - 0.5) * radius * 2,
          initialPosition[1],
          initialPosition[2] + (Math.random() - 0.5) * radius * 2
        );
      }
    } else {
      const dir = targetPos.current.clone().sub(currentPos).normalize();
      dir.y = 0;
      
      currentPos.add(dir.multiplyScalar(speed * delta));
      api.position.set(currentPos.x, currentPos.y, currentPos.z);
      
      const targetRotation = Math.atan2(dir.x, dir.z);
      currentRotationY.current = THREE.MathUtils.lerp(currentRotationY.current, targetRotation, 5 * delta);
      api.rotation.set(0, currentRotationY.current, 0);
    }
  });

  return (
    <group ref={ref}>
      {/* Body */}
      <mesh position={[0, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.8, 0.7, 1.6]} />
        <meshStandardMaterial color="#ffffff" roughness={1} />
      </mesh>
      {/* Black Spots */}
      <mesh position={[-0.41, 0, 0]} castShadow>
        <planeGeometry args={[0.6, 0.4]} />
        <meshStandardMaterial color="#222222" rotation={[0, -Math.PI / 2, 0]} roughness={1} />
      </mesh>
      <mesh position={[0.41, 0, -0.4]} castShadow>
        <planeGeometry args={[0.5, 0.5]} />
        <meshStandardMaterial color="#222222" rotation={[0, Math.PI / 2, 0]} roughness={1} />
      </mesh>
      {/* Head */}
      <group position={[0, 0.4, 0.9]}>
        <mesh castShadow receiveShadow>
          <boxGeometry args={[0.4, 0.5, 0.6]} />
          <meshStandardMaterial color="#ffffff" roughness={1} />
        </mesh>
        {/* Snout */}
        <mesh position={[0, -0.1, 0.35]} castShadow>
          <boxGeometry args={[0.3, 0.25, 0.2]} />
          <meshStandardMaterial color="#ffcfa1" roughness={1} />
        </mesh>
        {/* Horns */}
        <mesh position={[-0.15, 0.35, -0.1]} rotation={[0, 0, 0.2]} castShadow>
          <cylinderGeometry args={[0.02, 0.04, 0.2]} />
          <meshStandardMaterial color="#888" roughness={1} />
        </mesh>
        <mesh position={[0.15, 0.35, -0.1]} rotation={[0, 0, -0.2]} castShadow>
          <cylinderGeometry args={[0.02, 0.04, 0.2]} />
          <meshStandardMaterial color="#888" roughness={1} />
        </mesh>
        {/* Ears */}
        <mesh position={[-0.25, 0.1, -0.1]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <coneGeometry args={[0.05, 0.2]} />
          <meshStandardMaterial color="#fff" />
        </mesh>
        <mesh position={[0.25, 0.1, -0.1]} rotation={[0, 0, -Math.PI / 2]} castShadow>
          <coneGeometry args={[0.05, 0.2]} />
          <meshStandardMaterial color="#fff" />
        </mesh>
      </group>
      {/* Legs */}
      <mesh position={[-0.3, -0.4, 0.6]} castShadow><boxGeometry args={[0.15, 0.8, 0.15]} /><meshStandardMaterial color="#ddd" /></mesh>
      <mesh position={[0.3, -0.4, 0.6]} castShadow><boxGeometry args={[0.15, 0.8, 0.15]} /><meshStandardMaterial color="#ddd" /></mesh>
      <mesh position={[-0.3, -0.4, -0.6]} castShadow><boxGeometry args={[0.15, 0.8, 0.15]} /><meshStandardMaterial color="#ddd" /></mesh>
      <mesh position={[0.3, -0.4, -0.6]} castShadow><boxGeometry args={[0.15, 0.8, 0.15]} /><meshStandardMaterial color="#ddd" /></mesh>
      {/* Udder */}
      <mesh position={[0, -0.38, 0]} castShadow>
        <boxGeometry args={[0.3, 0.1, 0.4]} />
        <meshStandardMaterial color="#ffcfa1" />
      </mesh>
    </group>
  );
};

// Procedural DTC Bus (Delhi Transport Corporation - Green/Red)
export const DTCBus = ({ initialPosition, radius = 30, direction = 1, speed = 8, color = "#2e8b57" }) => {
  const [ref, api] = useBox(() => ({
    type: 'Kinematic',
    position: [initialPosition[0], 2.5, initialPosition[2]], // Tall
    args: [3, 5, 12],
    mass: 5000,
  }));

  const positionRef = useRef(new THREE.Vector3(initialPosition[0], 2.5, initialPosition[2]));

  useVehicleNavigation(initialPosition, radius, direction, speed, api, positionRef);

  return (
    <group ref={ref}>
      {/* Main Body */}
      <mesh castShadow receiveShadow>
        <boxGeometry args={[3, 4.5, 12]} />
        <meshStandardMaterial color={color} roughness={0.6} metalness={0.2} />
      </mesh>
      {/* White Roof Stripe */}
      <mesh position={[0, 2.26, 0]}>
        <boxGeometry args={[3, 0.05, 12]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
      {/* Side Windows */}
      <mesh position={[1.51, 0.5, 0]}>
        <boxGeometry args={[0.05, 1.5, 10]} />
        <meshStandardMaterial color="#111111" roughness={0.1} metalness={0.9} />
      </mesh>
      <mesh position={[-1.51, 0.5, 0]}>
        <boxGeometry args={[0.05, 1.5, 10]} />
        <meshStandardMaterial color="#111111" roughness={0.1} metalness={0.9} />
      </mesh>
      {/* Front Windshield */}
      <mesh position={[0, 0.5, 6.01]}>
        <boxGeometry args={[2.8, 1.8, 0.05]} />
        <meshStandardMaterial color="#111111" roughness={0.1} metalness={0.9} />
      </mesh>
      {/* Rear Windshield */}
      <mesh position={[0, 0.5, -6.01]}>
        <boxGeometry args={[2.8, 1.5, 0.05]} />
        <meshStandardMaterial color="#111111" roughness={0.1} metalness={0.9} />
      </mesh>
      {/* Headlights */}
      <mesh position={[1.2, -1.2, 6.01]}>
        <circleGeometry args={[0.2, 16]} />
        <meshBasicMaterial color="#ffffee" />
      </mesh>
      <mesh position={[-1.2, -1.2, 6.01]}>
        <circleGeometry args={[0.2, 16]} />
        <meshBasicMaterial color="#ffffee" />
      </mesh>
      {/* Taillights */}
      <mesh position={[1.2, -1.2, -6.01]}>
        <boxGeometry args={[0.3, 0.2, 0.05]} />
        <meshBasicMaterial color="#ff0000" />
      </mesh>
      <mesh position={[-1.2, -1.2, -6.01]}>
        <boxGeometry args={[0.3, 0.2, 0.05]} />
        <meshBasicMaterial color="#ff0000" />
      </mesh>
      {/* Wheels */}
      <mesh position={[-1.6, -2.5, 4]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.6, 0.6, 0.4, 24]} />
        <meshStandardMaterial color="#222" />
      </mesh>
      <mesh position={[1.6, -2.5, 4]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.6, 0.6, 0.4, 24]} />
        <meshStandardMaterial color="#222" />
      </mesh>
      <mesh position={[-1.6, -2.5, -4]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.6, 0.6, 0.4, 24]} />
        <meshStandardMaterial color="#222" />
      </mesh>
      <mesh position={[1.6, -2.5, -4]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.6, 0.6, 0.4, 24]} />
        <meshStandardMaterial color="#222" />
      </mesh>
    </group>
  );
};

// Pedestrian with simple walking animation
export const Pedestrian = ({ initialPosition, waypoints, speed = 2, color = "#ff5722", gender = "male", hasShoppingBag = false }) => {
  const currentWaypoint = useRef(0);
  const currentRotationY = useRef(0);
  
  const [ref, api] = useBox(() => ({
    type: 'Kinematic',
    position: [initialPosition[0], 0.9, initialPosition[2]],
    args: [0.6, 1.8, 0.6],
    mass: 70,
  }));

  const positionRef = useRef(new THREE.Vector3(initialPosition[0], 0.9, initialPosition[2]));
  const leftLeg = useRef();
  const rightLeg = useRef();
  const leftArm = useRef();
  const rightArm = useRef();
  const isWalking = useRef(true);

  // Register in global obstacles
  const uuid = useRef(THREE.MathUtils.generateUUID());
  useEffect(() => {
    return () => delete globalObstacles[uuid.current];
  }, []);

  useFrame((state, delta) => {
    globalObstacles[uuid.current] = positionRef.current.clone();
    isWalking.current = false;

    if (!waypoints || waypoints.length === 0) return;
    const target = waypoints[currentWaypoint.current];
    const currentPos = positionRef.current;
    const targetVec = new THREE.Vector3(target[0], currentPos.y, target[2]);
    const horizontalDist = new THREE.Vector2(currentPos.x, currentPos.z).distanceTo(new THREE.Vector2(targetVec.x, targetVec.z));

    if (horizontalDist < 0.5) {
      // Loop waypoints OR stop if reached the end of a non-looping path
      if (waypoints.length > 1) {
         currentWaypoint.current = (currentWaypoint.current + 1) % waypoints.length;
      }
    } else {
      const dir = targetVec.clone().sub(currentPos).normalize();
      if (dir.lengthSq() > 0.01) {
        currentPos.add(dir.multiplyScalar(speed * delta));
        api.position.set(currentPos.x, currentPos.y, currentPos.z);
        const targetRotation = Math.atan2(dir.x, dir.z);
        currentRotationY.current = THREE.MathUtils.lerp(currentRotationY.current, targetRotation, 10 * delta);
        api.rotation.set(0, currentRotationY.current, 0);
        isWalking.current = true;
      }
    }

    if (isWalking.current) {
      const t = state.clock.elapsedTime * speed * 2;
      if (leftLeg.current) leftLeg.current.rotation.x = Math.sin(t) * 0.5;
      if (rightLeg.current) rightLeg.current.rotation.x = Math.sin(t + Math.PI) * 0.5;
      if (leftArm.current) leftArm.current.rotation.x = Math.sin(t + Math.PI) * 0.5;
      if (rightArm.current) rightArm.current.rotation.x = Math.sin(t) * 0.5;
    } else {
      // Idle pose
      if (leftLeg.current) leftLeg.current.rotation.x = 0;
      if (rightLeg.current) rightLeg.current.rotation.x = 0;
      if (leftArm.current) leftArm.current.rotation.x = 0;
      if (rightArm.current) rightArm.current.rotation.x = 0;
    }
  });

  return (
    <group ref={ref}>
      {/* Torso */}
      <mesh position={[0, 0.2, 0]} castShadow receiveShadow>
        {gender === "female" ? <cylinderGeometry args={[0.2, 0.4, 0.8, 16]} /> : <boxGeometry args={[0.6, 0.8, 0.4]} />}
        <meshStandardMaterial color={color} />
      </mesh>
      {/* Head */}
      <group position={[0, 0.8, 0]}>
        <mesh castShadow receiveShadow><boxGeometry args={[0.4, 0.4, 0.4]} /><meshStandardMaterial color="#ffcfa1" /></mesh>
        {/* Hair */}
        {gender === "female" ? (
          <mesh position={[0, 0.2, -0.1]} castShadow><boxGeometry args={[0.45, 0.4, 0.5]} /><meshStandardMaterial color="#222" /></mesh>
        ) : (
          <mesh position={[0, 0.22, 0]} castShadow><boxGeometry args={[0.42, 0.1, 0.42]} /><meshStandardMaterial color="#222" /></mesh>
        )}
        {/* Eyes */}
        <mesh position={[-0.1, 0.05, 0.21]} castShadow><boxGeometry args={[0.05, 0.05, 0.01]} /><meshStandardMaterial color="#111" /></mesh>
        <mesh position={[0.1, 0.05, 0.21]} castShadow><boxGeometry args={[0.05, 0.05, 0.01]} /><meshStandardMaterial color="#111" /></mesh>
      </group>
      
      {/* Legs */}
      <group position={[-0.15, -0.2, 0]} ref={leftLeg}>
        <mesh position={[0, -0.4, 0]} castShadow><boxGeometry args={[0.25, 0.8, 0.25]} /><meshStandardMaterial color={gender === "female" ? color : "#333"} /></mesh>
        <mesh position={[0, -0.85, 0.05]} castShadow><boxGeometry args={[0.26, 0.1, 0.35]} /><meshStandardMaterial color="#111" /></mesh>
      </group>
      <group position={[0.15, -0.2, 0]} ref={rightLeg}>
        <mesh position={[0, -0.4, 0]} castShadow><boxGeometry args={[0.25, 0.8, 0.25]} /><meshStandardMaterial color={gender === "female" ? color : "#333"} /></mesh>
        <mesh position={[0, -0.85, 0.05]} castShadow><boxGeometry args={[0.26, 0.1, 0.35]} /><meshStandardMaterial color="#111" /></mesh>
      </group>
      
      {/* Arms */}
      <group position={[-0.4, 0.5, 0]} ref={leftArm}>
        <mesh position={[0, -0.3, 0]} castShadow><boxGeometry args={[0.2, 0.6, 0.2]} /><meshStandardMaterial color={color} /></mesh>
        <mesh position={[0, -0.65, 0]} castShadow><boxGeometry args={[0.18, 0.15, 0.18]} /><meshStandardMaterial color="#ffcfa1" /></mesh>
        {/* Shopping Bag in left hand */}
        {hasShoppingBag && (
           <group position={[0, -0.9, 0]}>
             <mesh castShadow><boxGeometry args={[0.1, 0.4, 0.4]} /><meshStandardMaterial color="#ffffff" /></mesh>
             <mesh position={[0, 0, 0.21]}><boxGeometry args={[0.01, 0.2, 0.2]} /><meshStandardMaterial color="#ff0000" /></mesh>
           </group>
        )}
      </group>
      <group position={[0.4, 0.5, 0]} ref={rightArm}>
        <mesh position={[0, -0.3, 0]} castShadow><boxGeometry args={[0.2, 0.6, 0.2]} /><meshStandardMaterial color={color} /></mesh>
        <mesh position={[0, -0.65, 0]} castShadow><boxGeometry args={[0.18, 0.15, 0.18]} /><meshStandardMaterial color="#ffcfa1" /></mesh>
      </group>
    </group>
  );
};

// Procedural Sedan Car
export const Sedan = ({ initialPosition, radius = 30, direction = 1, speed = 12, color = "#1e90ff" }) => {
  const [ref, api] = useBox(() => ({
    type: 'Kinematic',
    position: [initialPosition[0], 0.7, initialPosition[2]],
    args: [2, 1.4, 4],
    mass: 1500,
  }));

  const positionRef = useRef(new THREE.Vector3(initialPosition[0], 0.7, initialPosition[2]));

  useVehicleNavigation(initialPosition, radius, direction, speed, api, positionRef);

  return (
    <group ref={ref}>
      {/* Car Body Lower */}
      <mesh castShadow receiveShadow position={[0, -0.2, 0]}>
        <boxGeometry args={[2, 0.8, 4]} />
        <meshStandardMaterial color={color} roughness={0.4} metalness={0.6} />
      </mesh>
      {/* Car Cabin / Roof */}
      <mesh castShadow receiveShadow position={[0, 0.5, -0.2]}>
        <boxGeometry args={[1.6, 0.8, 2]} />
        <meshStandardMaterial color={color} roughness={0.4} metalness={0.6} />
      </mesh>
      {/* Windshield */}
      <mesh position={[0, 0.5, 0.81]} rotation={[-0.4, 0, 0]}>
        <planeGeometry args={[1.4, 0.8]} />
        <meshStandardMaterial color="#111" roughness={0.1} metalness={0.9} />
      </mesh>
      {/* Rear Window */}
      <mesh position={[0, 0.5, -1.21]} rotation={[0.4, 0, 0]}>
        <planeGeometry args={[1.4, 0.8]} />
        <meshStandardMaterial color="#111" roughness={0.1} metalness={0.9} />
      </mesh>
      {/* Side Windows */}
      <mesh position={[0.81, 0.5, -0.2]}>
        <boxGeometry args={[0.02, 0.6, 1.8]} />
        <meshStandardMaterial color="#111" roughness={0.1} metalness={0.9} />
      </mesh>
      <mesh position={[-0.81, 0.5, -0.2]}>
        <boxGeometry args={[0.02, 0.6, 1.8]} />
        <meshStandardMaterial color="#111" roughness={0.1} metalness={0.9} />
      </mesh>
      {/* Headlights */}
      <mesh position={[0.6, -0.1, 2.01]}>
        <boxGeometry args={[0.4, 0.2, 0.05]} />
        <meshBasicMaterial color="#ffffee" />
      </mesh>
      <mesh position={[-0.6, -0.1, 2.01]}>
        <boxGeometry args={[0.4, 0.2, 0.05]} />
        <meshBasicMaterial color="#ffffee" />
      </mesh>
      {/* Taillights */}
      <mesh position={[0.6, -0.1, -2.01]}>
        <boxGeometry args={[0.4, 0.2, 0.05]} />
        <meshBasicMaterial color="#ff0000" />
      </mesh>
      <mesh position={[-0.6, -0.1, -2.01]}>
        <boxGeometry args={[0.4, 0.2, 0.05]} />
        <meshBasicMaterial color="#ff0000" />
      </mesh>
      {/* Wheels */}
      <mesh position={[-1, -0.5, 1.2]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.3, 0.3, 0.2, 16]} />
        <meshStandardMaterial color="#222" />
      </mesh>
      <mesh position={[1, -0.5, 1.2]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.3, 0.3, 0.2, 16]} />
        <meshStandardMaterial color="#222" />
      </mesh>
      <mesh position={[-1, -0.5, -1.2]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.3, 0.3, 0.2, 16]} />
        <meshStandardMaterial color="#222" />
      </mesh>
      <mesh position={[1, -0.5, -1.2]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.3, 0.3, 0.2, 16]} />
        <meshStandardMaterial color="#222" />
      </mesh>
    </group>
  );
};

// Procedural Street Dog
export const StreetDog = ({ initialPosition, radius = 8, speed = 3 }) => {
  const [ref, api] = useBox(() => ({
    type: 'Kinematic',
    position: [initialPosition[0], 0.35, initialPosition[2]],
    args: [0.3, 0.7, 0.8],
    mass: 20,
  }));
  
  const positionRef = useRef(new THREE.Vector3(initialPosition[0], 0.35, initialPosition[2]));
  const currentRotationY = useRef(0);
  
  // Register in global obstacles
  const uuid = useRef(THREE.MathUtils.generateUUID());
  useEffect(() => {
    return () => delete globalObstacles[uuid.current];
  }, []);
  
  const targetPos = useRef(new THREE.Vector3(
    initialPosition[0] + (Math.random() - 0.5) * radius,
    0.35,
    initialPosition[2] + (Math.random() - 0.5) * radius
  ));
  
  const idleTimer = useRef(0);
  const isWalking = useRef(false);
  const legTime = useRef(0);
  const leftLeg = useRef();
  const rightLeg = useRef();

  useFrame((state, delta) => {
    globalObstacles[uuid.current] = positionRef.current.clone();

    if (idleTimer.current > 0) {
      idleTimer.current -= delta;
      isWalking.current = false;
      return;
    }

    const currentPos = positionRef.current;
    const horizontalDist = new THREE.Vector2(currentPos.x, currentPos.z).distanceTo(new THREE.Vector2(targetPos.current.x, targetPos.current.z));

    if (horizontalDist < 0.3) {
      if (Math.random() > 0.4) {
        idleTimer.current = 1 + Math.random() * 3;
      } else {
        targetPos.current.set(
          initialPosition[0] + (Math.random() - 0.5) * radius * 2,
          0.35,
          initialPosition[2] + (Math.random() - 0.5) * radius * 2
        );
      }
    } else {
      const dir = targetPos.current.clone().sub(currentPos).normalize();
      dir.y = 0;
      currentPos.add(dir.multiplyScalar(speed * delta));
      api.position.set(currentPos.x, currentPos.y, currentPos.z);
      
      const targetRotation = Math.atan2(dir.x, dir.z);
      currentRotationY.current = THREE.MathUtils.lerp(currentRotationY.current, targetRotation, 10 * delta);
      api.rotation.set(0, currentRotationY.current, 0);
      isWalking.current = true;
    }

    if (isWalking.current) {
      legTime.current += delta * speed * 5;
      if (leftLeg.current) leftLeg.current.rotation.x = Math.sin(legTime.current) * 0.5;
      if (rightLeg.current) rightLeg.current.rotation.x = Math.sin(legTime.current + Math.PI) * 0.5;
    }
  });

  return (
    <group ref={ref}>
      {/* Body */}
      <mesh castShadow receiveShadow><boxGeometry args={[0.3, 0.3, 0.8]} /><meshStandardMaterial color="#8b5a2b" roughness={1} /></mesh>
      {/* Tail */}
      <mesh position={[0, 0.1, -0.45]} rotation={[-Math.PI / 4, 0, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.04, 0.3]} />
        <meshStandardMaterial color="#8b5a2b" />
      </mesh>
      
      {/* Head */}
      <group position={[0, 0.25, 0.45]}>
        <mesh castShadow><boxGeometry args={[0.2, 0.25, 0.3]} /><meshStandardMaterial color="#8b5a2b" roughness={1} /></mesh>
        {/* Snout */}
        <mesh position={[0, -0.05, 0.15]} castShadow><boxGeometry args={[0.1, 0.1, 0.2]} /><meshStandardMaterial color="#4a2e15" roughness={1} /></mesh>
        {/* Nose */}
        <mesh position={[0, 0, 0.26]} castShadow><boxGeometry args={[0.03, 0.03, 0.03]} /><meshStandardMaterial color="#111" /></mesh>
        {/* Ears */}
        <mesh position={[-0.12, 0.15, -0.05]} rotation={[0, 0, Math.PI / 4]} castShadow>
          <coneGeometry args={[0.04, 0.15]} />
          <meshStandardMaterial color="#8B4513" />
        </mesh>
        <mesh position={[0.12, 0.15, -0.05]} rotation={[0, 0, -Math.PI / 4]} castShadow>
          <coneGeometry args={[0.04, 0.15]} />
          <meshStandardMaterial color="#8B4513" />
        </mesh>
      </group>

      <group ref={leftLeg}>
        <mesh position={[-0.1, -0.25, 0.3]} castShadow><boxGeometry args={[0.08, 0.4, 0.08]} /><meshStandardMaterial color="#8b5a2b" /></mesh>
        <mesh position={[0.1, -0.25, -0.3]} castShadow><boxGeometry args={[0.08, 0.4, 0.08]} /><meshStandardMaterial color="#8b5a2b" /></mesh>
      </group>
      <group ref={rightLeg}>
        <mesh position={[0.1, -0.25, 0.3]} castShadow><boxGeometry args={[0.08, 0.4, 0.08]} /><meshStandardMaterial color="#8b5a2b" /></mesh>
        <mesh position={[-0.1, -0.25, -0.3]} castShadow><boxGeometry args={[0.08, 0.4, 0.08]} /><meshStandardMaterial color="#8b5a2b" /></mesh>
      </group>
    </group>
  );
};
